import {useCallback, useContext, useEffect, useState} from 'react';
import {CustomDomainScreenContext} from './base';
import {useNavigate, useParams} from 'react-router-dom';
import {BoxWithTitle} from './list';
import {CustomDomainState} from '../../api/types/organization/CustomDomain';
import {CopyIcon} from 'lucide-react';
import {toast} from 'react-toastify';
import {Button} from '../../ui-components/Button/button';
import {DateTime} from 'luxon';

export function SettingsTitle(state: CustomDomainState) {
    switch (state) {
        case CustomDomainState.CNAME_PENDING:
            return 'Verify Domain';
        case CustomDomainState.CNAME_VERIFIED:
            return 'Provisioning Domain';
        case CustomDomainState.DOMAIN_PENDING:
            return 'Provisioning Domain';
        case CustomDomainState.DOMAIN_VERIFIED:
            return 'Provisioning Domain';
        case CustomDomainState.DOMAIN_READY:
            return 'Manage Domain';
        case CustomDomainState.DOMAIN_ERROR:
            return 'Domain Error';
    }
}

type LoadingState = 'idle' | 'pending' | 'resolved' | 'rejected';

export default function CustomDomainVerify() {
    const {api, list, onDomainUpdate} = useContext(CustomDomainScreenContext);
    const {hostname} = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<LoadingState>('idle');

    const [lastUpdate, setLastUpdate] = useState<number>(0);
    const [timer, setTimer] = useState<number | undefined>(undefined);

    const domain = list.find((d) => d.hostname === hostname);

    const setupTimer = useCallback(() => {
        if (timer) {
            clearTimeout(timer);
        }
        setTimer(
            window.setInterval(() => {
                api
                    ?.get(domain?.hostname || '')
                    .then((nd) => {
                        if (!!domain && nd.state !== domain.state) onDomainUpdate(nd);
                        setLastUpdate(Date.now());
                    })
                    .catch(() => {
                        toast('failed to check domain status', {type: 'error'});
                    });
            }, 60000),
        );
    }, [domain, timer, api, onDomainUpdate]);

    useEffect(() => {
        if (!domain) {
            navigate(`/domains`);
            return;
        }
        switch (domain.state) {
            case CustomDomainState.CNAME_PENDING:
                break;
            case CustomDomainState.CNAME_VERIFIED:
            case CustomDomainState.DOMAIN_PENDING:
            case CustomDomainState.DOMAIN_VERIFIED:
                setLastUpdate(Date.now());
                setupTimer();
                break;
            case CustomDomainState.DOMAIN_READY:
                navigate(`/domains/settings/${domain.hostname}`);
                break;
            case CustomDomainState.DOMAIN_ERROR:
            default:
                navigate(`/domains`);
                break;
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [domain, navigate, setLastUpdate, setupTimer, timer]);

    const onClick = useCallback(() => {
        setLoading('pending');
        api
            ?.verify(domain?.hostname || '')
            .then((nd) => {
                setLoading('resolved');
                toast('Domain ownership verified. Please wait while we provision your domain.', {type: 'success'});
                if (nd.state === CustomDomainState.DOMAIN_READY) {
                    navigate(`/domains/settings/${nd.hostname}?source=verify`);
                }
                onDomainUpdate(nd);
                setLastUpdate(Date.now());
                setupTimer();
            })
            .catch((e) => {
                setLoading('rejected');
                toast(`failed to verify domain, ${e.message}`, {type: 'error'});
            });
    }, [api, domain, navigate, onDomainUpdate, setLastUpdate, setupTimer]);

    return domain?.state === CustomDomainState.CNAME_PENDING ? (
        <BoxWithTitle title={`${SettingsTitle(domain.state)}: ${domain.hostname}`}>
            <div className={'flex flex-col p-6 leading-loose'}>
                <span className={'text-lg'}>Add a DNS CNAME record</span>
                <span>
                    Before we can verify <span className={'font-bold'}>{domain.hostname}</span>, you'll need to complete
                    the following steps:
                </span>
                <ol className={'list-decimal'}>
                    <li>
                        <div className={'flex flex-col'}>
                            <span className={'font-bold'}>
                                Create a CNAME record in your DNS configuration for the following hostname:
                            </span>
                            <div className={'flex flex-row items-center'}>
                                <input
                                    className={'rounded-md border border-gray-300 p-1'}
                                    value={domain.hostname}
                                    readOnly
                                />
                                <CopyIcon
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(domain.hostname)
                                            .then(() => {
                                                toast('Copied', {type: 'success'});
                                            })
                                            .catch(() => {
                                                toast('Failed to copy. Please copy the value manually.', {
                                                    type: 'error',
                                                });
                                            });
                                    }}
                                    className={'ml-2 cursor-pointer'}
                                />
                            </div>
                        </div>
                    </li>
                    <li>
                        <div className={'flex flex-col'}>
                            <span className={'font-bold'}>Use this for the value of the CNAME record:</span>
                            <div className={'flex flex-row items-center'}>
                                <input
                                    className={'rounded-md border border-gray-300 p-1'}
                                    value={domain.azure_cdn_endpoint?.hostname || ''}
                                    readOnly
                                />
                                <CopyIcon
                                    onClick={() => {
                                        navigator.clipboard
                                            .writeText(domain.azure_cdn_endpoint?.hostname || '')
                                            .then(() => {
                                                toast('Copied', {type: 'success'});
                                            })
                                            .catch(() => {
                                                toast('Failed to copy. Please copy the value manually.', {
                                                    type: 'error',
                                                });
                                            });
                                    }}
                                    className={'ml-2 cursor-pointer'}
                                />
                            </div>
                        </div>
                    </li>
                    <li>Wait until your DNS configuration changes. This could take up to 24 hours to propagate.</li>
                </ol>

                <Button
                    onClick={onClick}
                    data-disabled={loading === 'pending'}
                    disabled={loading === 'pending'}
                    className={'data-[disabled="true]:bg-gray-300'}>
                    {loading === 'pending' ? 'Verifying...' : 'Verify'}
                </Button>
            </div>
        </BoxWithTitle>
    ) : domain?.state === CustomDomainState.CNAME_VERIFIED ||
      domain?.state === CustomDomainState.DOMAIN_PENDING ||
      domain?.state === CustomDomainState.DOMAIN_VERIFIED ? (
        <BoxWithTitle title={`${SettingsTitle(domain.state)}: ${domain.hostname}`}>
            <div className={'flex flex-col p-6 leading-loose'}>
                <span className={'animate-pulse text-lg'}>Provisioning Domain...</span>
                <span>Please sit tight. This will take a few minutes.</span>
                <br />
                <span className={'text-sm italic text-gray-500'}>
                    Last checked: {DateTime.fromMillis(lastUpdate).toLocaleString(DateTime.DATETIME_MED)}
                </span>
            </div>
        </BoxWithTitle>
    ) : null;
}
